import React, { useState, useEffect } from 'react';

interface PasswordChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (user: any) => void;
  isFirstLogin?: boolean;
}

interface FormErrors {
  [key: string]: string;
}

const PasswordChangeModal: React.FC<PasswordChangeModalProps> = ({ 
  isOpen, 
  onClose, 
  onSuccess, 
  isFirstLogin = false 
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setError(null);
      setFormErrors({});
      setLoading(false);
    }
  }, [isOpen]);

  const validateForm = () => {
    const errors: FormErrors = {};

    if (!formData.currentPassword.trim()) {
      errors.currentPassword = isFirstLogin 
        ? 'Ditt midlertidige passord er påkrevd'
        : 'Nåværende passord er påkrevd';
    }

    if (!formData.newPassword.trim()) {
      errors.newPassword = 'Nytt passord er påkrevd';
    } else if (formData.newPassword.length < 8) {
      errors.newPassword = 'Nytt passord må være minst 8 tegn';
    } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/.test(formData.newPassword)) {
      errors.newPassword = 'Passordet må inneholde minst én stor bokstav, én liten bokstav, ett tall og ett spesialtegn';
    }

    if (!formData.confirmPassword.trim()) {
      errors.confirmPassword = 'Bekreft nytt passord';
    } else if (formData.newPassword !== formData.confirmPassword) {
      errors.confirmPassword = 'Passordene er ikke like';
    }

    if (formData.currentPassword === formData.newPassword) {
      errors.newPassword = 'Nytt passord må være forskjellig fra nåværende passord';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json();
        onSuccess(data.user);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to change password');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      setError(error instanceof Error ? error.message : 'Error changing password');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear field error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const getPasswordStrength = (password: string) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[@$!%*?&]/.test(password)) strength++;
    return strength;
  };

  const getStrengthLabel = (strength: number) => {
    switch (strength) {
      case 0:
      case 1:
        return { label: 'Svært svakt', class: 'very-weak' };
      case 2:
        return { label: 'Svakt', class: 'weak' };
      case 3:
        return { label: 'Middels', class: 'medium' };
      case 4:
        return { label: 'Sterkt', class: 'strong' };
      case 5:
        return { label: 'Meget sterkt', class: 'very-strong' };
      default:
        return { label: '', class: '' };
    }
  };

  const strength = getPasswordStrength(formData.newPassword);
  const strengthInfo = getStrengthLabel(strength);

  if (!isOpen) return null;

  return (
    <div className="password-change-overlay" onClick={isFirstLogin ? undefined : onClose}>
      <div className="password-change-modal" onClick={(e) => e.stopPropagation()}>
        <div className="password-change-header">
          <h2>
            {isFirstLogin ? 'Endre passord - Første innlogging' : 'Endre passord'}
          </h2>
          {!isFirstLogin && (
            <button 
              type="button"
              className="close-button" 
              onClick={onClose}
              aria-label="Lukk"
            >
              ×
            </button>
          )}
        </div>

        <div className="password-change-content">
          {isFirstLogin && (
            <div className="first-login-notice">
              <div className="alert alert-info">
                <span>🔐 Du må endre passord før du kan fortsette å bruke systemet.</span>
              </div>
            </div>
          )}

          {error && (
            <div className="alert alert-error">
              <span>⚠️ {error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="password-change-form">
            <div className="form-group">
              <label htmlFor="currentPassword" className="required">
                {isFirstLogin ? 'Midlertidig passord' : 'Nåværende passord'}
              </label>
              <input
                type="password"
                id="currentPassword"
                name="currentPassword"
                value={formData.currentPassword}
                onChange={handleInputChange}
                className={formErrors.currentPassword ? 'error' : ''}
                disabled={loading}
                placeholder={isFirstLogin ? 'Skriv inn ditt midlertidige passord' : 'Skriv inn nåværende passord'}
                autoComplete="current-password"
              />
              {formErrors.currentPassword && (
                <span className="error-message">{formErrors.currentPassword}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="newPassword" className="required">Nytt passord</label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleInputChange}
                className={formErrors.newPassword ? 'error' : ''}
                disabled={loading}
                placeholder="Skriv inn nytt passord"
                autoComplete="new-password"
              />
              {formErrors.newPassword && (
                <span className="error-message">{formErrors.newPassword}</span>
              )}
              
              {formData.newPassword && (
                <div className="password-strength">
                  <div className="strength-bar">
                    <div 
                      className={`strength-fill ${strengthInfo.class}`}
                      style={{ width: `${(strength / 5) * 100}%` }}
                    ></div>
                  </div>
                  <span className={`strength-label ${strengthInfo.class}`}>
                    Passordstyrke: {strengthInfo.label}
                  </span>
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword" className="required">Bekreft nytt passord</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                className={formErrors.confirmPassword ? 'error' : ''}
                disabled={loading}
                placeholder="Skriv inn nytt passord igjen"
                autoComplete="new-password"
              />
              {formErrors.confirmPassword && (
                <span className="error-message">{formErrors.confirmPassword}</span>
              )}
            </div>

            <div className="password-requirements">
              <h4>Passordkrav:</h4>
              <ul>
                <li className={formData.newPassword.length >= 8 ? 'valid' : ''}>
                  Minst 8 tegn
                </li>
                <li className={/[a-z]/.test(formData.newPassword) ? 'valid' : ''}>
                  Minst én liten bokstav (a-z)
                </li>
                <li className={/[A-Z]/.test(formData.newPassword) ? 'valid' : ''}>
                  Minst én stor bokstav (A-Z)
                </li>
                <li className={/\d/.test(formData.newPassword) ? 'valid' : ''}>
                  Minst ett tall (0-9)
                </li>
                <li className={/[@$!%*?&]/.test(formData.newPassword) ? 'valid' : ''}>
                  Minst ett spesialtegn (@$!%*?&)
                </li>
              </ul>
            </div>

            <div className="form-actions">
              {!isFirstLogin && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={(e) => {
                    e.preventDefault();
                    onClose();
                  }}
                  disabled={loading}
                >
                  Avbryt
                </button>
              )}
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'Endrer passord...' : 'Endre passord'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PasswordChangeModal;